import { atom } from 'jotai';
import { arrayMove } from '@dnd-kit/sortable';
import { generateDownloadUrl, generateShareUrl } from '../utils/urls';
import { microsecondsToSeconds, stripTimemarker } from '../utils/time';

export const selectedPlaylistAtom = atom();
selectedPlaylistAtom.debugLabel = 'selectedPlaylist';

export const currentPlaylistAtom = atom([]);
currentPlaylistAtom.debugLabel = 'currentPlaylist';

export const addToCurrentPlaylistAtom = atom(
  () => '',
  (get, set, newPlaylistItem) => {
    const { description, descriptionLineTwo, downloadUrl, shareUrl, url } =
      newPlaylistItem;

    const uniqueIdentifier = shareUrl;
    const entryExists = get(currentPlaylistAtom).find(
      (entry) => entry.uniqueIdentifier === uniqueIdentifier,
    );

    if (!entryExists) {
      set(currentPlaylistAtom, [
        ...get(currentPlaylistAtom),
        {
          description,
          descriptionLineTwo,
          downloadUrl,
          shareUrl,
          uniqueIdentifier,
          url,
        },
      ]);
    }
  },
);

export const findIndexByUniqueIdentifier = (playlist, uniqueIdentifier) =>
  playlist.findIndex((entry) => entry.uniqueIdentifier === uniqueIdentifier);

const findCurrentlyPlaying = (playlist) =>
  playlist.find((entry) => entry.isPlaying);
export const findCurrentlyPlayingIndex = (playlist) =>
  playlist.findIndex((entry) => entry.isPlaying);

export const currentlyPlayingAtom = atom(
  (get) => findCurrentlyPlaying(get(currentPlaylistAtom)) || { url: '' },
);

export const playlistEntriesWithClipFromAndClipToAtom = atom((get) =>
  get(currentPlaylistAtom).filter((entry) => entry.clipFrom && entry.clipTo),
);
export const playlistEntriesWithoutClipFromAndClipToAtom = atom((get) =>
  get(currentPlaylistAtom).filter((entry) => !entry.clipFrom || !entry.clipTo),
);

export const popFromPlaylistAtom = atom(
  (get) => get(currentPlaylistAtom),
  (get, set) => {
    set(currentPlaylistAtom, get(currentPlaylistAtom).slice(1));
  },
);

const updatePlayingIndex = (playlist, newIndex) => {
  const updatedPlaylist = playlist.map((entry, index) => ({
    ...entry,
    isPlaying: index === newIndex,
  }));
  return updatedPlaylist;
};

const skipBy = (playlist, skipValue) => {
  const currentlyPlayingIndex = findCurrentlyPlayingIndex(playlist);
  const newIndex = currentlyPlayingIndex + skipValue;

  return updatePlayingIndex(playlist, newIndex);
};
const skipToNextEntry = (playlist) => skipBy(playlist, 1);
const backToPreviousEntry = (playlist) => skipBy(playlist, -1);

export const skipAtom = atom(
  (get) => get(currentPlaylistAtom),
  (get, set) => {
    set(currentPlaylistAtom, skipToNextEntry(get(currentPlaylistAtom)));
  },
);
export const backAtom = atom(
  (get) => get(currentPlaylistAtom),
  (get, set) => {
    set(currentPlaylistAtom, backToPreviousEntry(get(currentPlaylistAtom)));
  },
);
export const playSpecificEntryAtom = atom(
  (get) => get(currentPlaylistAtom),
  (get, set, newIndex) => {
    set(
      currentPlaylistAtom,
      updatePlayingIndex(get(currentPlaylistAtom), newIndex),
    );
  },
);
export const playSpecificEntryByUrlAtom = atom(
  (get) => get(currentPlaylistAtom),
  (get, set, desiredUrl) => {
    const indexToMarkAsPlaying = get(currentPlaylistAtom).findIndex(
      (playlistEntry) => playlistEntry.url === desiredUrl,
    );
    set(
      currentPlaylistAtom,
      updatePlayingIndex(get(currentPlaylistAtom), indexToMarkAsPlaying),
    );
  },
);

export const emptyPlaylistAtom = atom(
  (get) => get(currentPlaylistAtom),
  (get, set) => {
    set(currentPlaylistAtom, []);
  },
);

// TO-DO: Change this to use a unique reference or index
export const deleteFromPlaylistAtom = atom(
  () => '',
  (get, set, urlOfEntryToBeRemoved) => {
    set(currentPlaylistAtom, [
      ...get(currentPlaylistAtom).filter(
        (playlistEntry) => playlistEntry.url !== urlOfEntryToBeRemoved,
      ),
    ]);
  },
);

export const updatePlaylistItemAtom = atom(
  () => '',
  (get, set, newPlaylistEntry) => {
    const updatedPlaylist = get(currentPlaylistAtom);
    const itemToUpdateIndex = get(currentPlaylistAtom).findIndex(
      (playlistEntry) => playlistEntry.url === newPlaylistEntry.url,
    );

    const { originalPlaylistEntry, clipFrom, clipTo } = newPlaylistEntry;

    const {
      sport,
      match_id: matchId,
      period,
      track,
      version,
    } = originalPlaylistEntry.video_clip_reference.video_reference;

    const shareUrl =
      sport && matchId && period
        ? `${generateShareUrl(
            sport,
            matchId,
            period,
            track,
            version,
            clipFrom,
          )}`
        : undefined;

    const downloadUrl =
      sport && matchId && period
        ? `${generateDownloadUrl(sport, matchId, period, clipFrom, clipTo)}`
        : undefined;

    const url = `${stripTimemarker(
      newPlaylistEntry.url,
    )}#t=${microsecondsToSeconds(clipFrom)}`;

    const uniqueIdentifier = `${url}${clipFrom}${clipTo}`;

    updatedPlaylist[itemToUpdateIndex] = {
      ...newPlaylistEntry,
      clipFrom,
      clipTo,
      downloadUrl,
      shareUrl,
      uniqueIdentifier,
      url,
    };

    set(currentPlaylistAtom, [...updatedPlaylist]);
  },
);

export const addToPlaylistWithTimestampsAtom = atom(
  () => {},
  (get, set, newPlaylistEntryData) => {
    const { playlistEntry } = newPlaylistEntryData;
    const clipFrom = playlistEntry.video_clip_reference.start_timestamp;
    const clipTo = playlistEntry.video_clip_reference.end_timestamp;

    const descriptionLineTwo =
      playlistEntry.descriptionLineTwo || playlistEntry.description_line_two;

    const url = `${playlistEntry.presigned_url}#t=${microsecondsToSeconds(
      clipFrom,
    )}`;

    const uniqueIdentifier = `${
      playlistEntry.video_clip_reference?.e_tag || url
    }${clipFrom}${clipTo}${playlistEntry.description}${descriptionLineTwo}`;

    const {
      sport,
      match_id: matchId,
      period,
      track,
      version,
    } = playlistEntry.video_clip_reference.video_reference;

    const shareUrl =
      sport && matchId && period
        ? `${generateShareUrl(
            sport,
            matchId,
            period,
            track,
            version,
            clipFrom,
          )}`
        : undefined;

    const downloadUrl =
      sport && matchId && period
        ? `${generateDownloadUrl(sport, matchId, period, clipFrom, clipTo)}`
        : undefined;

    const entryExists = get(currentPlaylistAtom).find(
      (entry) => entry.uniqueIdentifier === uniqueIdentifier,
    );

    if (!entryExists) {
      set(currentPlaylistAtom, [
        ...get(currentPlaylistAtom),
        {
          clipFrom,
          clipTo,
          description: playlistEntry.description,
          descriptionLineTwo,
          downloadUrl,
          originalPlaylistEntry: playlistEntry,
          shareUrl,
          uniqueIdentifier,
          url,
        },
      ]);
    }
  },
);

export const reorderPlaylistAtom = atom(
  () => '',
  (get, set, { activeId, overId }) => {
    set(currentPlaylistAtom, () => {
      const currentValue = get(currentPlaylistAtom);

      const oldIndex = currentValue.findIndex(
        (item) => item.uniqueIdentifier === activeId,
      );
      const newIndex = currentValue.findIndex(
        (item) => item.uniqueIdentifier === overId,
      );

      return arrayMove(currentValue, oldIndex, newIndex);
    });
  },
);
